import URI from 'urijs';
import { EntityType, StationSubType } from '../../../Constants';
import Container from '../../../Container';
import Chapter from '../../../dataServices/Chapter';
import Episode from '../../../dataServices/Episode';
import StandaloneChapter from '../../../dataServices/StandaloneChapter';
import Station from '../../../dataServices/Station';
import { type TPlayableObject } from '../../types';
import  { type IRewindStationMachineContext } from '../RewindStationMachine'
import { addTimes, isContentTypeAd, sFromTime, subtractTimes, timeFromMs, timeFromS } from './utils';

const serviceBus = Container;

export type StreamType = 'BROADCAST' | 'STANDALONE_CHAPTER';
export type PlayableStreamObject =
  | {
      streamType: 'BROADCAST';
      station: Station;
      episode?: Episode;
      standaloneChapter: undefined;
    }
  | {
      streamType: 'STANDALONE_CHAPTER';
      station: undefined;
      episode: undefined;
      standaloneChapter: StandaloneChapter;
    };
export type TLoadParams = {
  url: string;
  isPodcast: boolean;
  seekingInChapter: boolean,
}

export const findCurrentChapter = (
  chapters: Array<Chapter>| undefined,
  episode: Episode | undefined,
  time: number,
): number | undefined => {
  if (!chapters || !episode) {
    return;
  }

  const currentWallClockTime = addTimes(timeFromMs(episode.getStartTimeMillis()), timeFromS(time));

  for (let i = chapters.length - 1; i >= 0; i--) {
    const chapter = chapters[i];
    if (chapter.getStartOffset() <= sFromTime(currentWallClockTime)) {
      return i;
    }
  }
};

export const getCurrentChapterIndex = (
  ctx: IRewindStationMachineContext,
  { skipGracePeriod = false } = {},
) => {
  const { chapters, episode, elapsed, currentChapterIndex: oldCurrentChapterIndex, logger, metadata, lastSeekTimestamp } = ctx;
  let newIndex: number | undefined;

  if (metadata?.isLive && chapters && !lastSeekTimestamp) {
    newIndex = chapters.length - 1;
  } else {
    newIndex = findCurrentChapter(chapters, episode, elapsed ?? 0);
  }

  // After a seek, the HLS stream starts at a segment boundary that may not
  // align with the requested position. Hold the chapter index steady during
  // this window — the audio will catch up within seconds.
  if (
    !skipGracePeriod &&
    lastSeekTimestamp &&
    Date.now() - lastSeekTimestamp < 15000 &&
    oldCurrentChapterIndex !== undefined &&
    newIndex !== undefined &&
    newIndex !== oldCurrentChapterIndex
  ) {
    return { currentChapterIndex: oldCurrentChapterIndex };
  }

  if (newIndex !== oldCurrentChapterIndex) {
    logger?.info(`Current chapter index changed from ${oldCurrentChapterIndex} to ${newIndex}`);
  }

  return {
    currentChapterIndex: newIndex,
  };
}

export const getJitteredInterval = (interval: number, jitter: number): number =>
  interval + (Math.random() * 2 - 1) * jitter;

export const getAvailableContentEndOffset = (
  chapters: Array<Chapter> | undefined,
  episode: Episode | undefined,
): number => {
  if (!episode?.isLiveOnAir() || !chapters || chapters.length < 2) {
    return 0;
  }

  let lastMp3ChapterIndex = -1;
  for (let i = 0; i < chapters.length - 1; i++) {
    if (!chapters[i].getIsMP3()) {
      break;
    }

    lastMp3ChapterIndex = i;
  }

  if (lastMp3ChapterIndex < 0) {
    return 0;
  }

  const lastAvailableChapter = chapters[lastMp3ChapterIndex];
  const endAbsSec = lastAvailableChapter.getStartOffset() + (lastAvailableChapter.data?.duration ?? 0);
  return Math.max(0, endAbsSec - episode.getStartTimeSeconds());
};

/**
 * [OOP-740/OOP-738] MP3 Rewind plays one MP3 file per chapter, so the native
 * player reports `time` as seconds *within the current chapter's file* (it
 * resets to 0 on every chapter load). These helpers map that per-chapter time
 * onto one continuous show timeline for the scrub bar.
 *
 * TIMELINE MODEL: the scrub bar is the show's WALL-CLOCK span — chapter start
 * offsets are absolute Unix seconds, and the gaps between one chapter's end
 * (startOffset + duration) and the next chapter's startOffset are dynamically-
 * inserted ADS that are baked into the chapter MP3s. So the single timeline is
 * measured in wall-clock seconds relative to the first chapter's startOffset:
 * a position maps to a chapter via getChapterIndexFromOffset (last chapter whose
 * startOffset <= offset), and the intra-file offset is offset - startOffset
 * (which runs into the ad tail when the position falls in a gap). `elapsed` and
 * the absolute `requestedOffset` are therefore linearly related:
 *   requestedOffset = chapters[0].startOffset + elapsed.
 * The API `duration` is content-only, so it's fuzzy vs the real (ad-inclusive)
 * MP3 length — product accepts that fuzziness; the sequence of MP3s is what's
 * authoritative. The synthetic getLiveChapter (duration 0) is excluded from the
 * span. See memory: mp3-rewind-chapter-gaps-are-ads.
 */

// Pick the chapter whose start offset is the latest one at/under the requested
// absolute (Unix seconds) offset. This is the chapter whose MP3 is loaded, and
// is the single source of truth for the current chapter during MP3 playback.
//
// Returns NO_CHAPTER_MATCH (-1) when nothing matches — no chapters, no offset,
// or the offset sits before every chapter's start. Callers must handle the
// no-match case deliberately; the previous caller-supplied `fallback` parameter
// let stale context indexes (often out of range for the CURRENT chapters array)
// flow through unvalidated.
export const NO_CHAPTER_MATCH = -1;

export const getChapterIndexFromOffset = (
  chapters: Array<Chapter> | undefined,
  requestedOffset: number | undefined,
): number => {
  if (requestedOffset === undefined || !chapters || chapters.length === 0) {
    return NO_CHAPTER_MATCH;
  }
  for (let i = chapters.length - 1; i >= 0; i--) {
    if (chapters[i].getStartOffset() <= requestedOffset) {
      return i;
    }
  }
  return NO_CHAPTER_MATCH;
};

// [OOP-738] Show-relative start of the chapter at `index`, on the WALL-CLOCK
// timeline: chapter[index].startOffset - chapter[0].startOffset. (Named
// "timeline start" — NOT getChapterStartOffset — to avoid confusion with
// Chapter.getStartOffset(), which is the ABSOLUTE Unix-seconds offset.)
//
// The scrub bar is the wall-clock span of the show (content + the ad "gaps"
// between chapters — see getChaptersTotalDuration), NOT the sum of content
// durations. Chapter start offsets are absolute Unix seconds that already
// include those gaps, so the chapter's position on the scrub bar is simply its
// offset minus the first chapter's offset. For CONTIGUOUS chapters (no gaps)
// this equals the old sum-of-prior-durations, so contiguous behaviour is
// unchanged; for real gapped data it correctly accounts for the ad time.
export const getChapterTimelineStart = (
  chapters: Array<Chapter> | undefined,
  index: number,
): number => {
  if (!chapters || chapters.length === 0) {
    return 0;
  }
  const clampedIndex = Math.max(0, Math.min(index, chapters.length - 1));
  return Math.max(0, chapters[clampedIndex].getStartOffset() - chapters[0].getStartOffset());
};

// [OOP-910] Total scrub-bar length = the show's WALL-CLOCK span, from the first
// chapter's start to the last REAL chapter's end (startOffset + duration). This
// includes the ad gaps between chapters (the real MP3s play content + inserted
// ads, so the wall-clock span is what the user actually hears). The synthetic
// live-edge chapter appended by getLiveChapter has duration 0 and sits +10s past
// the last real chapter, so it's excluded here (only chapters with a real
// duration count) to avoid overshooting the span by that spacer.
export const getChaptersTotalDuration = (chapters: Array<Chapter> | undefined): number => {
  if (!chapters || chapters.length === 0) {
    return 0;
  }
  const firstStart = chapters[0].getStartOffset();
  let lastRealEnd = 0;
  for (const chapter of chapters) {
    const duration = chapter.getDuration();
    if (duration > 0) {
      lastRealEnd = Math.max(lastRealEnd, chapter.getStartOffset() + duration);
    }
  }
  if (lastRealEnd === 0) {
    return 0;
  }
  return Math.max(0, lastRealEnd - firstStart);
};

// [OOP-738] Seconds into the chapter's own MP3 file for a given absolute
// requested offset (Unix seconds): intraOffset = requestedOffset - startOffset.
//
// Floored at 0, but the upper bound is NOT clamped to the content `duration`.
// The real MP3 file is content + an inserted ad (the wall-clock "gap" before the
// next chapter), so a seek that lands in that gap resolves to THIS chapter (via
// getChapterIndexFromOffset) with an intra offset > duration — i.e. into the ad
// tail — which is exactly where we want to seek. The value is naturally bounded
// by the next chapter's startOffset (anything at/after it resolves to the next
// chapter), so it can't run arbitrarily far.
export const getChapterSeekOffset = (
  chapters: Array<Chapter> | undefined,
  index: number,
  requestedOffset: number | undefined,
): number => {
  const chapter = chapters?.[index];
  if (!chapter || requestedOffset === undefined) {
    return 0;
  }
  return Math.max(0, requestedOffset - chapter.getStartOffset());
};

// [OOP-738] Convert a show-relative seek time (seconds from the start of the
// chapter timeline, i.e. what the scrubber/skip/chapter-tap UI sends) into an
// absolute (Unix seconds) offset on that SAME chapter timeline.
//
// Deliberately NOT normalizeResumePoint: that helper anchors on
// entity.getStartTimeSeconds() and validates the result against the episode's
// real broadcast window (`>= entityEnd`, `> now`), resetting "corrupted" values
// to the episode start. But the chapters are anchored at chapter[0].startOffset
// and — with fixture durations (OOP-794) that don't match the real broadcast —
// their startOffsets routinely fall outside the real window. Every forward seek
// then got "corrected" back to the episode start (symptom: any scrub/skip
// forward restarted the chapter from 0:00).
export const getOffsetFromSeekTime = (
  chapters: Array<Chapter> | undefined,
  time: number,
): number | undefined => {
  if (!chapters || chapters.length === 0) {
    return undefined;
  }
  // Exact conversion — deliberately NOT normalizeResumePoint's `ceil + 1`
  // boundary nudge: with the anchor-exact math here a chapter-start seek lands
  // on that chapter already (getChapterIndexFromOffset uses `<=`), and the
  // nudge made every skip/scrub land up to 2 seconds AHEAD of the requested
  // position. Values that are already absolute epoch seconds pass through.
  const year2000 = new Date('2000').getTime() / 1000;
  return time >= year2000 ? time : chapters[0].getStartOffset() + time;
};

// Map the per-chapter native player time onto the continuous show timeline:
// elapsed = (start of current chapter on the show timeline) + (time within the chapter).
export const mapMp3PlayerTime = (
  ctx: IRewindStationMachineContext,
  time: number,
): { elapsed: number } => {
  const { chapters, currentChapterIndex, metadata, requestedOffset } = ctx;
  // Live edge: wall-clock mapping needs BOTH anchors (OOP-741); exitRewindMode
  // clears episode/datumTime on rewind→live handoffs, so without them pin to
  // the timeline end instead of anchoring on epoch zero. [OOP-737]
  if (metadata?.isLive) {
    const entity = ctx.standaloneChapter ?? ctx.episode;
    if (entity && metadata.datumTime) {
      return mapPlayerTime(ctx, time);
    }
    return { elapsed: getChaptersTotalDuration(chapters) };
  }
  const matched = getChapterIndexFromOffset(chapters, requestedOffset);
  // No-match (offset before every chapter / absent) anchors on the first
  // chapter — getChapterTimelineStart clamps internally, so 0 is safe.
  const index = currentChapterIndex ?? (matched === NO_CHAPTER_MATCH ? 0 : matched);
  const elapsed = getChapterTimelineStart(chapters, index) + time;
  // [OOP-738] Floor at 0 but do NOT clamp the upper bound to
  // totalDuration. The upper clamp is redundant — PlayerStateConnector already
  // clamps elapsed/progress/remaining for the scrubber display — and it broke
  // every end-of-content guard: isPlayingAfterShowEnd / isPlayingAfterEntityEnd
  // / isFinishedCompletePastShow test `elapsed > total`, which a clamped elapsed
  // can never satisfy. That silently disabled past-show auto-advance (OOP-729)
  // and standalone-chapter end detection, and starved the last-chapter→live
  // transition. Let elapsed run slightly past total at end-of-file so those
  // guards fire; the UI layer keeps the scrubber pinned.
  return { elapsed: Math.max(0, elapsed) };
};

export const  getLiveChapter = (episode: Episode, chapters: Array<Chapter>) => {
  let startOffset: number;
  if (chapters.length > 0) {
    const lastElement = chapters[chapters.length - 1];
    const lastDuration = lastElement.data?.duration || 0;
    startOffset = lastElement.data.startOffset + lastDuration + 10; // NOTICE: added 10 seconds for the difference between chapters for correct switching
  } else {
    startOffset = episode.getStartTimeSeconds();
  }

  return new Chapter({ startOffset, title: `Part ${chapters.length + 1}` });
}

export const getLoadParams = (ctx: IRewindStationMachineContext): TLoadParams => {
  const { logger, requestedOffset, standaloneChapter, source, seekingInChapter } = ctx;

  if (!source) {
    throw new Error('No playable streamUrl found (SSR)');
  }

  const url = new URI(source.url);
  const isStandaloneChapter = ctx.streamType === 'STANDALONE_CHAPTER';

  let isInChapter = seekingInChapter;

  // NOTE:  No requestOffset signifies a non-live rewind stream
  if (requestedOffset !== undefined && !ctx.playLive) {
    url.setQuery('startTs', requestedOffset);

    if (isStandaloneChapter) {
      const endTs = standaloneChapter?.getEndTimeSeconds();
      url.addQuery('endTs', endTs);
    }

    // NOTE: [CCS-2636] - VOD - only add mode=vod if the episode is rewindable and we are playing a VOD stream/not live
    if (isVod(ctx)) {
      url.addQuery('mode', 'vod');
      // adds a check to see if the user is on the live chapter, but not the live stream
      // preventing the stream from requesting an ad while just behind live
      if (!isLive(ctx) && isLiveChapter(ctx)) {
        url.addQuery('inChapter', true)
      } else {
        url.addQuery('inChapter', seekingInChapter ?? true);
      }
      isInChapter = true;

    } else if (ctx.retryCount && ctx.retryCount > 0) {
      // We can use this when trying to reconnect so a user doesn't immediately get an ad when they reconnect
      url.addQuery('inChapter', true);
      isInChapter = true;
    } else {
      url.addQuery('inChapter', seekingInChapter !== false);
      isInChapter = seekingInChapter !== false;
    }

    logger?.debug(
      `Requesting SSR stream with wall clock offset ${requestedOffset}, ${
        seekingInChapter ? 'within the current chapter' : 'across a chapter boundary'
      }`,
    );
  }

  url.addQuery('newMeta', 'true');

  logger?.info(`Requesting SSR stream with URL ${url.valueOf()}`);
  
  return {
    url: url.valueOf(),
    isPodcast: source.type !== 'm3u8',
    seekingInChapter: isInChapter ?? false,
  };
}

// [OOP-756] True when `timestampSec` (absolute epoch seconds) is a past
// moment actually covered by `episode`'s window. This is the guard against
// EpisodeList.getEpisode's silent last-entry fallback: getEpisode resolves
// SOME episode for any timestamp, so "the resolved episode doesn't cover the
// timestamp" is how an out-of-schedule timestamp manifests. Shared by the
// deeplink validators below and both rewind machines' getSchedule guards so
// the validation and playback layers can't drift.
// [OOP-756] Small allowance for device-vs-server clock skew: a share link
// created "now" on the sender's clock can be a few seconds in the future on
// a receiver whose clock runs behind. Without it, fresh links are rejected
// with "no longer available" for no user-visible reason.
export const CLOCK_SKEW_TOLERANCE_SEC = 30;

export const episodeCoversTimestamp = (
  episode: Episode | undefined,
  timestampSec: number,
): boolean =>
  !!episode &&
  timestampSec <= Date.now() / 1000 + CLOCK_SKEW_TOLERANCE_SEC &&
  episode.getStartTimeSeconds() <= timestampSec &&
  timestampSec < episode.getEndTimeSeconds();

// [OOP-756] Cheap synchronous validity checks for a rewind deeplink
// timestamp against a station target — no schedule fetch. Rejects malformed
// (NaN/Infinity), non-positive, and future timestamps, and stations that
// can't rewind at all. Schedule coverage needs the async check below.
export const isValidRewindTimestampSync = (
  station: Station,
  timestampSec: number,
): boolean =>
  Number.isFinite(timestampSec) &&
  timestampSec > 0 &&
  timestampSec <= Date.now() / 1000 + CLOCK_SKEW_TOLERANCE_SEC &&
  station.isRewindable();

// [OOP-756] Validates a rewind deeplink timestamp against an EPISODE target —
// the shape "share at this moment" links produce (actionContentId is the
// broadcast episode, actionOffset is epoch seconds). Fully synchronous: the
// episode's own window is the schedule coverage.
export const isValidRewindTimestampForEpisode = (
  episode: Episode,
  timestampSec: number,
): boolean =>
  Number.isFinite(timestampSec) &&
  timestampSec > 0 &&
  episode.isRewindable() &&
  episodeCoversTimestamp(episode, timestampSec);

// [OOP-756] Full validation of a Rewind deeplink timestamp (absolute epoch
// seconds) against a station's schedule, so the deeplink layer can surface
// an error message. A timestamp is valid when it is a finite past time
// covered by a rewindable episode in the station's schedule.
//
// Schedule-fetch failures return true (fail open): a network blip is not an
// invalid link, and both rewind machines' own out-of-range guards in
// getSchedule still fall back to live if the timestamp turns out to be bad —
// this check only decides whether to explain that fallback to the user.
export const isValidRewindTimestamp = async (
  station: Station,
  timestampSec: number,
): Promise<boolean> => {
  if (!isValidRewindTimestampSync(station, timestampSec)) {
    return false;
  }

  try {
    const schedules = await station.getSchedules();
    const episode = await schedules?.getEpisode(timestampSec * 1000);
    return !!episode && episode.isRewindable() && episodeCoversTimestamp(episode, timestampSec);
  } catch {
    return true;
  }
};

/**
 * Convert a dataObject into a strongly typed object that we can use in RewindStationMachine
 */
export const getPlayableDataObject = async (
  dataObject: TPlayableObject,
): Promise<PlayableStreamObject | undefined> => {
  let station = dataObject instanceof Station ? dataObject : undefined;
  const episode = dataObject instanceof Episode ? dataObject : undefined;
  const standaloneChapter = dataObject instanceof StandaloneChapter ? dataObject : undefined;

  // Standalone chapter
  if (standaloneChapter) {
    return {
      streamType: 'STANDALONE_CHAPTER',
      station: undefined,
      episode: undefined,
      standaloneChapter,
    };
  }

  // Get parent station if playing AmperWave radio broadcast by episode
  const stationId = episode?.getParentStation()?.getId();
  if (stationId) {
    try {
      station = (await serviceBus.dataServices.getContentObject(stationId)) as Station;
    } catch (error) {
      // Log error but continue playback without station data
      // This prevents 404s from stopping the stream
      console.error(`Failed to fetch station ${stationId}:`, error);
      station = undefined;
    }
  }

  // Play AmperWave radio broadcast
  if (
    station &&
    station.getEntityType() === EntityType.STATION &&
    station.getEntitySubtype() === StationSubType.BROADCAST &&
    station.isRewindable()
  ) {
    return {
      streamType: 'BROADCAST',
      station,
      episode,
      standaloneChapter: undefined,
    };
  }

  // No playable object
  return;
};

const isLive = (ctx: IRewindStationMachineContext) => {
  return ctx.metadata?.isLive ?? false;
};

const isLiveChapter = (ctx: IRewindStationMachineContext) => {
  const lastChapter = ctx.chapters?.[ctx.chapters.length - 1];
  const endOfLastChapter = (lastChapter?.data.startOffset ?? 0) + (lastChapter?.data.duration ?? 0);
  const isRequestOffsetGreaterThanEndOfLastChapter = (ctx.requestedOffset ?? 0) > endOfLastChapter;

  return isRequestOffsetGreaterThanEndOfLastChapter;
};

const isVod = (ctx: IRewindStationMachineContext) => {
  return !!ctx.player.vodFeatureEnabled && ctx.episode?.isRewindable() && !ctx.playLive;
};

export const mapPlayerTime = (ctx: IRewindStationMachineContext, time: number) => {
  const { metadata, episode, standaloneChapter } = ctx;

  const entity = standaloneChapter ?? episode;

  const entityStartTime = timeFromMs(entity?.getStartTimeMillis() ?? 0);

  // ATL is StreamGuys which has no datumTime, so use entityStartTime
  // datumTime is essentially the startTs / resume point when playing a rewind stream relative to unix epoch in seconds
  // datumTime is usually equal to startTs so that's the best way to think of it
  // time is the elapsed time since we started playing the stream
  // so datumTime + time = the current playback timestamp relative to unix epoch
  const datumTime = metadata?.datumTime ? timeFromS(metadata.datumTime) : entityStartTime;

  const elapsed = sFromTime(addTimes(subtractTimes(datumTime, entityStartTime), timeFromS(time)));

  return { elapsed };
};

/**
 * Normalizes absolute (epoch) and relative (episode start) resume points for use
 * as elapsed player time in seconds. Converts as follows:
 * Broadcast - to absolute elapsed times, because we use AmperWave
 */
export const normalizeResumePoint = ({
  resumePoint,
  entity,
  streamType,
}: {
  resumePoint?: number;
  entity: Episode | StandaloneChapter | undefined;
  streamType: StreamType;
}): number | undefined => {
  // Resume points not allowed for non-rewindable episodes
  if (!entity?.isRewindable()) {
    return undefined;
  }

  const now = Date.now() / 1000;
  const year2000 = new Date('2000').getTime() / 1000;
  const entityStart = entity.getStartTimeSeconds();
  const entityEnd = entity.getEndTimeSeconds();

  // Reset corrupted resume points
  if (!resumePoint || resumePoint < 0 || resumePoint >= entityEnd || resumePoint > now) {
    // If the resume point is corrupted and the requested entity is LIVE, play LIVE
    if (streamType === 'BROADCAST' && entityEnd > now) {
      return undefined;
    }
    resumePoint = entityStart;
  }

  // TODO: The chapter selection from the UI doesn't like decimals too much
  // We could just send in the non-truncated version of this decimal, but the UI will stutter a bit
  // So we are doing this to get to the nearest higher millisecond + 1
  // to make sure we don't overlap with the possible ending of another chapter
  const resumePointRoundedUp = Math.ceil(resumePoint) + 1;
  const absoluteResumePoint =
    resumePointRoundedUp >= year2000 ? resumePointRoundedUp : entityStart + resumePointRoundedUp;

  if (streamType === 'BROADCAST') {
    return absoluteResumePoint;
  }

  if (streamType === 'STANDALONE_CHAPTER') {
    return absoluteResumePoint;
  }
};

export function performSeek(ctx: IRewindStationMachineContext, time: number): IRewindStationMachineContext {
  const { metadata, episode, standaloneChapter, chapters, currentChapterIndex } = ctx;
  const entity = standaloneChapter ?? episode;
  // StreamGuys uses 'seconds since episode start'
  const requestedOffset = normalizeResumePoint({
    resumePoint: time,
    entity,
    streamType: ctx.streamType,
  });

  const episodeSeekingInChapter =
    currentChapterIndex !== undefined &&
    findCurrentChapter(chapters, episode, time) === currentChapterIndex;

  return {
      ...ctx,
      seekingInChapter: isVod(ctx) ? true : episodeSeekingInChapter,
      requestedOffset,
      elapsed: time,
      datumTimeProtected: false,
      lastSeekTimestamp: Date.now(),
      metadata: {
        ...metadata,
        // ATL is StreamGuys, so keep datumTime undefined
        datumTime: metadata?.datumTime ? requestedOffset : undefined,
        isLive: false,
      },
    };
}

export function saveResumePoint(ctx: IRewindStationMachineContext, resumePoint: number) : IRewindStationMachineContext {
  return {
      ...ctx,
      requestedOffset:
          ctx.player.vodFeatureEnabled && isContentTypeAd(ctx.metadata?.contentType)
          ? ctx.requestedOffset
          : resumePoint,
  };
}

export function saveResumePoints(ctx: IRewindStationMachineContext, shouldSaveOnServer = true) {
  const { elapsed, personalizationProvider, episode, standaloneChapter, item } =
      ctx;
  const entity = episode ?? standaloneChapter;
  if (!entity || elapsed === undefined) {
      return ctx;
  }

  ctx.timeSinceLastSavedResumePoint = Date.now();

  const playbackOffset = entity.getStartTimeSeconds() + elapsed;

  const entityPlayback = { contentId: entity.getId(), playbackOffset};
  const itemPlayback = item.data.id ? { contentId: item.data.id as string, playbackOffset } : undefined;
  const playbacks  = itemPlayback ? [ entityPlayback, itemPlayback  ] : [ entityPlayback ];


  void personalizationProvider.setPlaybacks(playbacks, shouldSaveOnServer );

  return saveResumePoint(ctx, playbackOffset);
}
